(() => {
class PairedProductShowcase extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToCarousel) return;

    this.track = this.querySelector('.paired-product-showcase__track');
    this.slides = Array.from(this.querySelectorAll('[data-slide-index]'));
    this.previousButton = this.querySelector('[name="previous"]');
    this.nextButton = this.querySelector('[name="next"]');
    this.dots = Array.from(this.querySelectorAll('[data-slide-to]'));
    this.currentSlideElement = this.querySelector('[data-current-slide]');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.currentIndex = 0;

    if (!this.track || this.slides.length === 0) return;

    this.isConnectedToCarousel = true;
    this.onPreviousClick = () => this.goTo(this.currentIndex - 1);
    this.onNextClick = () => this.goTo(this.currentIndex + 1);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    this.onScrollEnd = this.handleScrollEnd.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);
    this.onResize = () => this.goTo(this.currentIndex, false);

    this.previousButton?.addEventListener('click', this.onPreviousClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.dotHandlers = this.dots.map((dot) => {
      const handler = () => this.goTo(Number(dot.dataset.slideTo));
      dot.addEventListener('click', handler);
      return handler;
    });
    this.addEventListener('keydown', this.onKeydown);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('scrollend', this.onScrollEnd);
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.resizeObserver = new ResizeObserver(this.onResize);
    this.resizeObserver.observe(this.track);
    this.setActive(0);
  }

  disconnectedCallback() {
    if (!this.isConnectedToCarousel) return;

    this.previousButton?.removeEventListener('click', this.onPreviousClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.dots.forEach((dot, index) => dot.removeEventListener('click', this.dotHandlers[index]));
    this.removeEventListener('keydown', this.onKeydown);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('scrollend', this.onScrollEnd);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.scrollEndTimer);
    this.isConnectedToCarousel = false;
  }

  goTo(index, animate = true) {
    const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const nextSlide = this.slides[nextIndex];
    if (!nextSlide) return;

    this.setActive(nextIndex);
    this.track.scrollTo({
      left: nextSlide.offsetLeft,
      behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'auto',
    });
  }

  setActive(index) {
    this.currentIndex = index;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.toggleAttribute('inert', !isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });

    this.dots.forEach((dot, dotIndex) => {
      if (dotIndex === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    if (this.previousButton) this.previousButton.hidden = index === 0;
    if (this.nextButton) this.nextButton.hidden = index === this.slides.length - 1;
    if (this.currentSlideElement) this.currentSlideElement.textContent = String(index + 1);
  }

  handleKeydown(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.currentIndex - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.currentIndex + 1);
    }
  }

  handleScroll() {
    window.clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = window.setTimeout(this.onScrollEnd, 120);
  }

  handleScrollEnd() {
    window.clearTimeout(this.scrollEndTimer);
    const closestIndex = this.slides.reduce((closest, slide, index) => {
      const currentDistance = Math.abs(slide.offsetLeft - this.track.scrollLeft);
      const closestDistance = Math.abs(this.slides[closest].offsetLeft - this.track.scrollLeft);
      return currentDistance < closestDistance ? index : closest;
    }, 0);

    this.setActive(closestIndex);
  }

  handleBlockSelect(event) {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    const selectedIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
    if (selectedIndex >= 0) this.goTo(selectedIndex);
  }
}

if (!customElements.get('paired-product-showcase')) {
  customElements.define('paired-product-showcase', PairedProductShowcase);
}
})();
